import { Injectable } from '@angular/core';
import { Firestore, collection, addDoc, GeoPoint, query, orderBy, limit, getDocs, setDoc, docData, getFirestore } from '@angular/fire/firestore';
import { arrayRemove, arrayUnion, deleteDoc, doc, getDoc, onSnapshot, updateDoc, where, writeBatch } from 'firebase/firestore';
import { from, map, Observable, of, Subject } from 'rxjs';
import { StorageService } from './storage.service';
import { deleteObject, Storage } from '@angular/fire/storage';
import { ref } from 'firebase/storage';
import { AudioMetadata, VideoMetadata, VideoStats } from '../../interfaces/user-generated-content.model';
import { AnonymousUserService } from '../user services/anonymous-user.service';
import { Auth } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root'
})

export class FirestoreService {

  // Map of active listeners to avoid duplicate subscriptions
  private activeListeners: Map<string, () => void> = new Map();

  // Event emitters for real-time updates
  public newPhotoAdded = new Subject<any>();
  public newVideoAdded = new Subject<any>();
  public newAudioAdded = new Subject<any>();
  public photoDeleted = new Subject<string>(); // Will emit document ID
  public videoDeleted = new Subject<string>();
  public audioDeleted = new Subject<string>();

  likeStatus: 'none' | 'liked' | 'disliked' = 'none';
  likeCount: number = 0;
  dislikeCount: number = 0;

  constructor(
    private _firestore: Firestore,
    private _auth: Auth,
    private _storageService: StorageService,
    private _storage: Storage,
    private _anonymousUserService: AnonymousUserService
  ) { }

  getFirestore(): Firestore {
    return this._firestore;
  }

  async savePhotoMetadata(
    photoUrl: string,
    latitude: number,
    longitude: number,
    userId: string,
    isCompressed: boolean = false
  ): Promise<string> {
    try {

      const deviceId = this._anonymousUserService.currentUserId;

      if (!deviceId) {
        throw new Error('User not initialized');
      }

      let attempt = 0;
      const maxRetries = 3;
      let lastError;

      while (attempt < maxRetries) {
        try {
          // Save photo metadata to Firestore
          const photosCollection = collection(this._firestore, 'mapPhotos');
          const docRef = await addDoc(photosCollection, {
            photoUrl: photoUrl,
            location: new GeoPoint(latitude, longitude),
            timestamp: new Date(),
            userId: userId,
            likes: [],
            dislikes: [],
            ...(isCompressed ? { isCompressed: true } : {})
          });
          return docRef.id;
        } catch (error) {
          lastError = error;
          attempt++;
          // Log the error for debugging
          console.error(`[FirestoreService] Save failed (attempt ${attempt}/${maxRetries}):`, error);

          if (attempt >= maxRetries) {
            console.error(`[FirestoreService] Max retries reached. Giving up.`);
            throw error;
          }

          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000;
          //  console.log(`[FirestoreService] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // This line is technically unreachable, but TypeScript might complain without it
      throw new Error('Failed to save photo metadata after maximum retries');
    } catch (error) {
      console.error('[FirestoreService] Error in savePhotoMetadata:', error);
      throw error;
    }
  }

  async saveVideoMetadata(
    videoUrl: string,
    latitude: number,
    longitude: number,
    userId: string,  // This should be the device ID from UserService
    isCompressed: boolean = false
  ): Promise<string> {
    try {
      const deviceId = this._anonymousUserService.currentUserId;

      if (!deviceId) {
        throw new Error('User not initialized');
      }

      let attempt = 0;
      const maxRetries = 3;
      let lastError;

      while (attempt < maxRetries) {
        try {
          // Save video metadata to Firestore
          const videosCollection = collection(this._firestore, 'mapVideos');
          const docRef = await addDoc(videosCollection, {
            videoUrl: videoUrl,
            location: new GeoPoint(latitude, longitude),
            timestamp: new Date(),
            userId: userId,
            deviceId: deviceId,
            likes: [],
            dislikes: [],
            ...(isCompressed ? { isCompressed: true } : {})
          });
          return docRef.id;
        } catch (error) {
          lastError = error;
          attempt++;
          // Log the error for debugging
          console.error(`[FirestoreService] Save failed (attempt ${attempt}/${maxRetries}):`, error);

          if (attempt >= maxRetries) {
            console.error(`[FirestoreService] Max retries reached. Giving up.`);
            throw error;
          }

          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000;
          //  console.log(`[FirestoreService] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // This line is technically unreachable, but TypeScript might complain without it
      throw new Error('Failed to save video metadata after maximum retries');
    } catch (error) {
      console.error('[FirestoreService] Error in saveVideoMetadata:', error);
      throw error;
    }
  }

  async saveAudioMetadata(
    audioUrl: string,
    latitude: number,
    longitude: number,
    userId: string,  // This should be the device ID from UserService
    isCompressed: boolean = false
  ): Promise<string> {
    try {
      const deviceId = this._anonymousUserService.currentUserId;

      if (!deviceId) {
        throw new Error('User not initialized');
      }

      let attempt = 0;
      const maxRetries = 3;
      let lastError;

      while (attempt < maxRetries) {
        try {
          // Save audio metadata to Firestore
          const audiosCollection = collection(this._firestore, 'mapAudios');
          const docRef = await addDoc(audiosCollection, {
            audioUrl: audioUrl,
            location: new GeoPoint(latitude, longitude),
            timestamp: new Date(),
            userId: userId,
            deviceId: deviceId,
            likes: [],
            dislikes: [],
            ...(isCompressed ? { isCompressed: true } : {})
          });
          return docRef.id;
        } catch (error) {
          lastError = error;
          attempt++;
          // Log the error for debugging
          console.error(`[FirestoreService] Save failed (attempt ${attempt}/${maxRetries}):`, error);

          if (attempt >= maxRetries) {
            console.error(`[FirestoreService] Max retries reached. Giving up.`);
            throw error;
          }

          // Exponential backoff
          const delay = Math.pow(2, attempt) * 1000;
          //  console.log(`[FirestoreService] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // This line is technically unreachable, but TypeScript might complain without it
      throw new Error('Failed to save video metadata after maximum retries');
    } catch (error) {
      console.error('[FirestoreService] Error in saveVideoMetadata:', error);
      throw error;
    }
  }

  async getDocById(docId: string): Promise<any> {
    const deviceId = this._anonymousUserService.currentUserId;

    if (!deviceId) {
      throw new Error('User not initialized');
    }

    const collections = ['mapPhotos', 'mapAudios', 'mapVideos'];

    for (const collection of collections) {
      try {
        const docRef = doc(this._firestore, collection, docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            collection: collection, // Adding the collection name to know where it was found
            ...data,
            // Add a flag to indicate if this user is the publisher
            isPublisher: data['userId'] === deviceId
          };
        }
      } catch (error) {
        console.error(`[FirestoreService] Error fetching document from ${collection}:`, error);
        // Continue to the next collection instead of throwing error immediately
      }
    }

    // If we reach here, the document wasn't found in any collection
    console.warn('[FirestoreService] Document not found in any collection:', docId);
    throw new Error('Document not found');
  }

  async getVideoById(videoId: string): Promise<any> {
    const deviceId = this._anonymousUserService.currentUserId;

    if (!deviceId) {
      throw new Error('User not initialized');
    }

    try {
      const videoRef = doc(this._firestore, 'mapVideos', videoId);
      const videoSnap = await getDoc(videoRef);

      if (videoSnap.exists()) {
        const data = videoSnap.data();
        //console.log('[FirestoreService] Document data:', data);
        return {
          id: videoSnap.id,
          ...data,
          // Add a flag to indicate if this user is the publisher
          isPublisher: data['userId'] === deviceId
        };
      } else {
        console.warn('[FirestoreService] video document not found:', videoId);
        throw new Error('video not found');
      }
    } catch (error) {
      console.error('[FirestoreService] Error fetching video:', error);
      throw error;
    }
  }

  async getAudioById(audioId: string): Promise<any> {
    const deviceId = this._anonymousUserService.currentUserId;

    if (!deviceId) {
      throw new Error('User not initialized');
    }

    try {
      const audioRef = doc(this._firestore, 'mapAudios', audioId);
      const audioSnap = await getDoc(audioRef);

      if (audioSnap.exists()) {
        const data = audioSnap.data();
        //console.log('[FirestoreService] Document data:', data);
        return {
          id: audioSnap.id,
          ...data,
          // Add a flag to indicate if this user is the publisher
          isPublisher: data['userId'] === deviceId
        };
      } else {
        console.warn('[FirestoreService] audio document not found:', audioId);
        throw new Error('audio not found');
      }
    } catch (error) {
      console.error('[FirestoreService] Error fetching audio:', error);
      throw error;
    }
  }

  async getRecentAudios(maxCount?: number, deleteOldAudios: boolean = false) {
    try {
      //console.log(`[FirestoreService] getRecentAudios called with deleteOldAudios=${deleteOldaudios}`);

      const audiosCollection = collection(this._firestore, 'mapAudios');

      // Calculate the timestamp for 24 hours ago
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      // console.log(`[FirestoreService] Filtering audios older than: ${new Date(oneDayAgo).toISOString()}`);

      // Get all audios ordered by timestamp
      let q = query(audiosCollection, orderBy('timestamp', 'desc'));

      // Apply limit if provided
      if (maxCount) {
        q = query(q, limit(maxCount));
      }

      // Get the query snapshot
      const querySnapshot = await getDocs(q);
      // console.log(`[FirestoreService] Total audios retrieved: ${querySnapshot.size}`);

      // Create arrays for filtered docs and old docs
      const filteredDocs: typeof querySnapshot.docs[0][] = [];
      const oldDocs: typeof querySnapshot.docs[0][] = [];

      // Process each document and filter based on time adjustment
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const timestamp = data['timestamp']?.toMillis ? data['timestamp'].toMillis() : data['timestamp'];
        const timeAdjustment = data['timeAdjustment'] || 0;

        // Adjust the timestamp by the timeAdjustment (in seconds)
        const adjustedTimestamp = timestamp + (timeAdjustment * 1000);

        const photoDate = new Date(adjustedTimestamp).toISOString();

        if (adjustedTimestamp >= oneDayAgo) {
          // Add to filtered results if newer than 24 hours after adjustment
          filteredDocs.push(doc);
          // console.log(`[FirestoreService] Recent photo: ${doc.id}, date: ${photoDate}`);
        } else {
          // Add to old docs if older than 24 hours
          oldDocs.push(doc);
          // console.log(`[FirestoreService] Old photo: ${doc.id}, date: ${photoDate}`);
        }
      });

      // console.log(`[FirestoreService] Found ${filteredDocs.length} recent photos and ${oldDocs.length} old photos`);

      // If deleteOldPhotos flag is true, delete all old photos
      if (deleteOldAudios) {
        //console.log(`[FirestoreService] deleteOldPhotos flag is true`);

        if (oldDocs.length > 0) {
          // console.log(`[FirestoreService] Deleting ${oldDocs.length} old photos`);

          // Delete old photos in parallel using Promise.all
          await Promise.all(
            oldDocs.map(async (doc) => {
              const data = doc.data();
              const userId = data['userId'];

              try {
                // First delete from Storage if there's a userId
                if (userId) {
                  // Construct the file path based on the naming convention seen in photoUrl
                  const filename = `profile_${userId}.jpg`;
                  const filePath = `userPictures/${userId}/${filename}`;

                  console.log(`[FirestoreService] Attempting to delete from Storage: ${filePath}`);

                  // Delete from Storage
                  await this._storageService.deletePhotoFromStorageByPath(filePath);
                } else {
                  //  console.log(`[FirestoreService] No userId found for photo: ${doc.id}, skipping Storage deletion`);
                }

                // Then delete from Firestore
                // console.log(`[FirestoreService] Deleting Firestore document: ${doc.id}`);
                await this.deletePhotoFromFirestore(doc.id);

                // console.log(`[FirestoreService] Successfully deleted old photo: ${doc.id}`);
              } catch (error) {
                console.error(`[FirestoreService] Error deleting old photo: ${doc.id}`, error);
                // Continue with other deletions even if one fails
              }
            })
          );

          //  console.log(`[FirestoreService] Finished processing deletions for old photos`);
        } else {
          // console.log(`[FirestoreService] No old photos to delete`);
        }
      } else {
        // console.log(`[FirestoreService] deleteOldPhotos flag is false, skipping deletion`);
      }

      // Create a modified querySnapshot with a properly implemented forEach method
      const modifiedSnapshot = {
        ...querySnapshot,
        docs: filteredDocs,
        size: filteredDocs.length,
        empty: filteredDocs.length === 0,
        forEach: (callback: (result: typeof querySnapshot.docs[0], i: number) => void) => {
          filteredDocs.forEach((doc, i) => callback(doc, i));
        }
      };

      //  console.log(`[FirestoreService] Returning ${filteredDocs.length} recent photos`);
      return modifiedSnapshot;

    } catch (error) {
      console.error('[FirestoreService] Error fetching recent photos:', error);
      throw error;
    }
  }

  async getRecentPhotos(maxCount?: number, deleteOldPhotos: boolean = false) {
    try {
      //console.log(`[FirestoreService] getRecentPhotos called with deleteOldPhotos=${deleteOldPhotos}`);

      const photosCollection = collection(this._firestore, 'mapPhotos');

      // Calculate the timestamp for 24 hours ago
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      // console.log(`[FirestoreService] Filtering photos older than: ${new Date(oneDayAgo).toISOString()}`);

      // Get all photos ordered by timestamp
      let q = query(photosCollection, orderBy('timestamp', 'desc'));

      // Apply limit if provided
      if (maxCount) {
        q = query(q, limit(maxCount));
      }

      // Get the query snapshot
      const querySnapshot = await getDocs(q);
      // console.log(`[FirestoreService] Total photos retrieved: ${querySnapshot.size}`);

      // Create arrays for filtered docs and old docs
      const filteredDocs: typeof querySnapshot.docs[0][] = [];
      const oldDocs: typeof querySnapshot.docs[0][] = [];

      // Process each document and filter based on time adjustment
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const timestamp = data['timestamp']?.toMillis ? data['timestamp'].toMillis() : data['timestamp'];
        const timeAdjustment = data['timeAdjustment'] || 0;

        // Adjust the timestamp by the timeAdjustment (in seconds)
        const adjustedTimestamp = timestamp + (timeAdjustment * 1000);

        const photoDate = new Date(adjustedTimestamp).toISOString();

        if (adjustedTimestamp >= oneDayAgo) {
          // Add to filtered results if newer than 24 hours after adjustment
          filteredDocs.push(doc);
          // console.log(`[FirestoreService] Recent photo: ${doc.id}, date: ${photoDate}`);
        } else {
          // Add to old docs if older than 24 hours
          oldDocs.push(doc);
          // console.log(`[FirestoreService] Old photo: ${doc.id}, date: ${photoDate}`);
        }
      });

      // console.log(`[FirestoreService] Found ${filteredDocs.length} recent photos and ${oldDocs.length} old photos`);

      // If deleteOldPhotos flag is true, delete all old photos
      if (deleteOldPhotos) {
        //console.log(`[FirestoreService] deleteOldPhotos flag is true`);

        if (oldDocs.length > 0) {
          // console.log(`[FirestoreService] Deleting ${oldDocs.length} old photos`);

          // Delete old photos in parallel using Promise.all
          await Promise.all(
            oldDocs.map(async (doc) => {
              const data = doc.data();
              const userId = data['userId'];

              try {
                // First delete from Storage if there's a userId
                if (userId) {
                  // Construct the file path based on the naming convention seen in photoUrl
                  const filename = `profile_${userId}.jpg`;
                  const filePath = `userPictures/${userId}/${filename}`;

                  // console.log(`[FirestoreService] Attempting to delete from Storage: ${filePath}`);

                  // Delete from Storage
                  await this._storageService.deletePhotoFromStorageByPath(filePath);
                } else {
                  //  console.log(`[FirestoreService] No userId found for photo: ${doc.id}, skipping Storage deletion`);
                }

                // Then delete from Firestore
                // console.log(`[FirestoreService] Deleting Firestore document: ${doc.id}`);
                await this.deletePhotoFromFirestore(doc.id);

                // console.log(`[FirestoreService] Successfully deleted old photo: ${doc.id}`);
              } catch (error) {
                console.error(`[FirestoreService] Error deleting old photo: ${doc.id}`, error);
                // Continue with other deletions even if one fails
              }
            })
          );

          //  console.log(`[FirestoreService] Finished processing deletions for old photos`);
        } else {
          // console.log(`[FirestoreService] No old photos to delete`);
        }
      } else {
        // console.log(`[FirestoreService] deleteOldPhotos flag is false, skipping deletion`);
      }

      // Create a modified querySnapshot with a properly implemented forEach method
      const modifiedSnapshot = {
        ...querySnapshot,
        docs: filteredDocs,
        size: filteredDocs.length,
        empty: filteredDocs.length === 0,
        forEach: (callback: (result: typeof querySnapshot.docs[0], i: number) => void) => {
          filteredDocs.forEach((doc, i) => callback(doc, i));
        }
      };

      //  console.log(`[FirestoreService] Returning ${filteredDocs.length} recent photos`);
      return modifiedSnapshot;

    } catch (error) {
      console.error('[FirestoreService] Error fetching recent photos:', error);
      throw error;
    }
  }


  public async getLatestTimestamp(collectionName?: string): Promise<number | undefined> {
    try {
      const collections = collectionName
        ? [collectionName]
        : ['mapPhotos', 'mapVideos', 'mapAudios'];

      const timestampPromises = collections.map(async (colName) => {
        const col = collection(this._firestore, colName);
        const q = query(col, orderBy('timestamp', 'desc'), limit(1));

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          return 0; // Return 0 for empty collections
        }

        const doc = snapshot.docs[0];
        const data = doc.data();

        // Handle Firestore Timestamp objects
        if (data['timestamp'] && typeof data['timestamp'].toMillis === 'function') {
          return data['timestamp'].toMillis();
        }
        // Handle numeric timestamps
        else if (typeof data['timestamp'] === 'number') {
          return data['timestamp'];
        }

        return 0; // Fallback
      });

      // Wait for all promises to resolve
      const timestamps = await Promise.all(timestampPromises);

      // Find the maximum timestamp
      const maxTimestamp = Math.max(...timestamps);

      // Return undefined if no valid timestamps found
      return maxTimestamp > 0 ? maxTimestamp : undefined;
    } catch (error) {
      console.error(`[FirestoreService] Error getting latest timestamp:`, error);
      return undefined;
    }
  }


  async deletePhotoFromFirestore(photoId: string): Promise<void> {
    try {
      const photoRef = doc(this._firestore, 'mapPhotos', photoId);
      const photoSnap = await getDoc(photoRef);
      console.log('[FirestoreService] Photo data:', photoSnap.data());
      console.log('[FirestoreService] Current auth UID:', this._auth.currentUser?.uid);
      await deleteDoc(photoRef);
    } catch (error) {
      console.error('[FirestoreService] Error deleting photo:', error);
      throw error;
    }
  }

  async initializePhotoLikeStatus(photoId: string, userId: string): Promise<void> {
    try {
      const photoRef = doc(this._firestore, 'mapPhotos', photoId);
      const photoDoc = await getDoc(photoRef);

      if (photoDoc.exists()) {
        const photoData = photoDoc.data();
        const likes = photoData['likes'] || [];
        const dislikes = photoData['dislikes'] || [];

        // Set counts
        this.likeCount = likes.length;
        this.dislikeCount = dislikes.length;

        // Set user's status
        if (likes.includes(userId)) {
          this.likeStatus = 'liked';
        } else if (dislikes.includes(userId)) {
          this.likeStatus = 'disliked';
        } else {
          this.likeStatus = 'none';
        }
      }
    } catch (error) {
      console.error('Error initializing like status:', error);
    }
  }

  async likePhoto(photoId: string, userId: string): Promise<void> {
    try {
      const photoRef = doc(this._firestore, 'mapPhotos', photoId);
      // Determine the action based on current status
      if (this.likeStatus === 'liked') {
        // User is unliking
        await updateDoc(photoRef, {
          likes: arrayRemove(userId),
          timeAdjustment: 0 // Reset time adjustment
        });
        this.likeCount--;
        this.likeStatus = 'none';
      } else {
        // User is liking - subtract 1 hour to make it appear older
        await updateDoc(photoRef, {
          likes: arrayUnion(userId),
          dislikes: arrayRemove(userId), // Remove from dislikes if previously disliked
          timeAdjustment: 3600 // +1 hour in seconds
        });
        this.likeCount++;
        // If previously disliked, reduce dislike count
        if (this.likeStatus === 'disliked') {
          this.dislikeCount--;
        }
        this.likeStatus = 'liked';
      }
    } catch (error) {
      console.error('Error liking photo:', error);
    }
  }

  async dislikePhoto(photoId: string, userId: string): Promise<void> {
    try {
      const photoRef = doc(this._firestore, 'mapPhotos', photoId);
      // Determine the action based on current status
      if (this.likeStatus === 'disliked') {
        // User is un-disliking
        await updateDoc(photoRef, {
          dislikes: arrayRemove(userId),
          timeAdjustment: 0 // Reset time adjustment
        });
        this.dislikeCount--;
        this.likeStatus = 'none';
      } else {
        // User is disliking - add 1 hour to make it appear newer
        await updateDoc(photoRef, {
          dislikes: arrayUnion(userId),
          likes: arrayRemove(userId), // Remove from likes if previously liked
          timeAdjustment: -3600 // +1 hour in seconds
        });
        this.dislikeCount++;
        // If previously liked, reduce like count
        if (this.likeStatus === 'liked') {
          this.likeCount--;
        }
        this.likeStatus = 'disliked';
      }
    } catch (error) {
      console.error('Error disliking photo:', error);
    }
  }

  async getRecentVideos(maxCount?: number, deleteOldVideos: boolean = false) {
    try {

      const videosCollection = collection(this._firestore, 'mapVideos');

      // Calculate the timestamp for 24 hours ago
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      // console.log(`[FirestoreService] Filtering videos older than: ${new Date(oneDayAgo).toISOString()}`);

      // Get all videos ordered by timestamp
      let q = query(videosCollection, orderBy('timestamp', 'desc'));

      // Apply limit if provided
      if (maxCount) {
        q = query(q, limit(maxCount));
      }

      // Get the query snapshot
      const querySnapshot = await getDocs(q);
      // console.log(`[FirestoreService] Total videos retrieved: ${querySnapshot.size}`);

      // Create arrays for filtered docs and old docs
      const filteredDocs: typeof querySnapshot.docs[0][] = [];
      const oldDocs: typeof querySnapshot.docs[0][] = [];

      // Process each document and filter based on time adjustment
      querySnapshot.forEach(doc => {
        const data = doc.data();
        const timestamp = data['timestamp']?.toMillis ? data['timestamp'].toMillis() : data['timestamp'];
        const timeAdjustment = data['timeAdjustment'] || 0;

        // Adjust the timestamp by the timeAdjustment (in seconds)
        const adjustedTimestamp = timestamp + (timeAdjustment * 1000);

        const videosDate = new Date(adjustedTimestamp).toISOString();

        if (adjustedTimestamp >= oneDayAgo) {
          // Add to filtered results if newer than 24 hours after adjustment
          filteredDocs.push(doc);
          // console.log(`[FirestoreService] Recent videos: ${doc.id}, date: ${videosDate}`);
        } else {
          // Add to old docs if older than 24 hours
          oldDocs.push(doc);
          // console.log(`[FirestoreService] Old videos: ${doc.id}, date: ${videosDate}`);
        }
      });

      // console.log(`[FirestoreService] Found ${filteredDocs.length} recent videos and ${oldDocs.length} old videoss`);

      // If deleteOldvideoss flag is true, delete all old videos
      if (deleteOldVideos) {
        //console.log(`[FirestoreService] deleteOldvideoss flag is true`);

        if (oldDocs.length > 0) {
          // console.log(`[FirestoreService] Deleting ${oldDocs.length} old videoss`);

          // Delete old videoss in parallel using Promise.all
          await Promise.all(
            oldDocs.map(async (doc) => {
              const data = doc.data();
              const userId = data['userId'];

              try {
                // First delete from Storage if there's a userId
                if (userId) {
                  // Construct the file path based on the naming convention seen in videoUrl
                  const filename = `profile_${userId}.jpg`;
                  const filePath = `videos/${userId}/${filename}`;

                  console.log(`[FirestoreService] Attempting to delete from Storage: ${filePath}`);

                  // Delete from Storage
                  await this._storageService.deleteVideoFromStorageByPath(filePath);
                } else {
                  //  console.log(`[FirestoreService] No userId found for videos: ${doc.id}, skipping Storage deletion`);
                }

                // Then delete from Firestore
                // console.log(`[FirestoreService] Deleting Firestore document: ${doc.id}`);
                await this.deleteVideosFromFirestore(doc.id);

                // console.log(`[FirestoreService] Successfully deleted old videos: ${doc.id}`);
              } catch (error) {
                console.error(`[FirestoreService] Error deleting old videos: ${doc.id}`, error);
                // Continue with other deletions even if one fails
              }
            })
          );

          //  console.log(`[FirestoreService] Finished processing deletions for old videoss`);
        } else {
          // console.log(`[FirestoreService] No old videoss to delete`);
        }
      } else {
        // console.log(`[FirestoreService] deleteOldvideoss flag is false, skipping deletion`);
      }

      // Create a modified querySnapshot with a properly implemented forEach method
      const modifiedSnapshot = {
        ...querySnapshot,
        docs: filteredDocs,
        size: filteredDocs.length,
        empty: filteredDocs.length === 0,
        forEach: (callback: (result: typeof querySnapshot.docs[0], i: number) => void) => {
          filteredDocs.forEach((doc, i) => callback(doc, i));
        }
      };

      //  console.log(`[FirestoreService] Returning ${filteredDocs.length} recent videos`);
      return modifiedSnapshot;

    } catch (error) {
      console.error('[FirestoreService] Error fetching recent videos:', error);
      throw error;
    }
  }


  async initializeVideoLikeStatus(videoId: string, userId: string): Promise<void> {
    try {
      const videoRef = doc(this._firestore, 'mapVideos', videoId);
      const videoDoc = await getDoc(videoRef);

      if (videoDoc.exists()) {
        const videoData = videoDoc.data();
        const likes = videoData['likes'] || [];
        const dislikes = videoData['dislikes'] || [];

        // Set counts
        this.likeCount = likes.length;
        this.dislikeCount = dislikes.length;

        // Set user's status
        if (likes.includes(userId)) {
          this.likeStatus = 'liked';
        } else if (dislikes.includes(userId)) {
          this.likeStatus = 'disliked';
        } else {
          this.likeStatus = 'none';
        }
      }
    } catch (error) {
      console.error('Error initializing like status:', error);
    }
  }

  async likeVideo(videoId: string, userId: string): Promise<void> {
    try {
      const videoRef = doc(this._firestore, 'mapVideos', videoId);
      // Determine the action based on current status
      if (this.likeStatus === 'liked') {
        // User is unliking
        await updateDoc(videoRef, {
          likes: arrayRemove(userId),
          timeAdjustment: 0 // Reset time adjustment
        });
        this.likeCount--;
        this.likeStatus = 'none';
      } else {
        // User is liking - subtract 1 hour to make it appear older
        await updateDoc(videoRef, {
          likes: arrayUnion(userId),
          dislikes: arrayRemove(userId), // Remove from dislikes if previously disliked
          timeAdjustment: 3600 // +1 hour in seconds
        });
        this.likeCount++;
        // If previously disliked, reduce dislike count
        if (this.likeStatus === 'disliked') {
          this.dislikeCount--;
        }
        this.likeStatus = 'liked';
      }
    } catch (error) {
      console.error('Error liking video:', error);
    }
  }

  async dislikeVideo(videoId: string, userId: string): Promise<void> {
    try {
      const videoRef = doc(this._firestore, 'mapVideos', videoId);
      // Determine the action based on current status
      if (this.likeStatus === 'disliked') {
        // User is un-disliking
        await updateDoc(videoRef, {
          dislikes: arrayRemove(userId),
          timeAdjustment: 0 // Reset time adjustment
        });
        this.dislikeCount--;
        this.likeStatus = 'none';
      } else {
        // User is disliking - add 1 hour to make it appear newer
        await updateDoc(videoRef, {
          dislikes: arrayUnion(userId),
          likes: arrayRemove(userId), // Remove from likes if previously liked
          timeAdjustment: -3600 // +1 hour in seconds
        });
        this.dislikeCount++;
        // If previously liked, reduce like count
        if (this.likeStatus === 'liked') {
          this.likeCount--;
        }
        this.likeStatus = 'disliked';
      }
    } catch (error) {
      console.error('Error disliking video:', error);
    }
  }



  async getVideoMetadata(videoId: string): Promise<VideoMetadata | null> {
    try {
      // Create a reference to the document
      const videoDocRef = doc(this._firestore, 'mapVideos', videoId);

      // Get the document
      const docSnap = await getDoc(videoDocRef);

      // Check if document exists
      if (docSnap.exists()) {
        const data = docSnap.data() as any; // First cast to any to avoid type issues
        // Properly handle the timestamp conversion
        return {
          ...data,
          docId: docSnap.id,
          // Convert Firestore timestamps to Date objects with proper type handling
          createdAt: data.createdAt instanceof Date ?
            data.createdAt :
            (data.createdAt && typeof data.createdAt.toDate === 'function' ?
              data.createdAt.toDate() :
              new Date()),
          expirationTime: data.expirationTime instanceof Date ?
            data.expirationTime :
            (data.expirationTime && typeof data.expirationTime.toDate === 'function' ?
              data.expirationTime.toDate() :
              new Date())
        };
      }
      return null;
    } catch (error) {
      console.error('[VideoService] Error getting video metadata:', error);
      return null;
    }
  }

  async likeAudio(audioId: string, userId: string): Promise<void> {
    try {
      const audioRef = doc(this._firestore, 'mapAudios', audioId);
      // Determine the action based on current status
      if (this.likeStatus === 'liked') {
        // User is unliking
        await updateDoc(audioRef, {
          likes: arrayRemove(userId),
          timeAdjustment: 0 // Reset time adjustment
        });
        this.likeCount--;
        this.likeStatus = 'none';
      } else {
        // User is liking - subtract 1 hour to make it appear older
        await updateDoc(audioRef, {
          likes: arrayUnion(userId),
          dislikes: arrayRemove(userId), // Remove from dislikes if previously disliked
          timeAdjustment: 3600 // +1 hour in seconds
        });
        this.likeCount++;
        // If previously disliked, reduce dislike count
        if (this.likeStatus === 'disliked') {
          this.dislikeCount--;
        }
        this.likeStatus = 'liked';
      }
    } catch (error) {
      console.error('Error liking audio:', error);
    }
  }

  async dislikeAudio(audioId: string, userId: string): Promise<void> {
    try {
      const audioRef = doc(this._firestore, 'mapAudios', audioId);
      // Determine the action based on current status
      if (this.likeStatus === 'disliked') {
        // User is un-disliking
        await updateDoc(audioRef, {
          dislikes: arrayRemove(userId),
          timeAdjustment: 0 // Reset time adjustment
        });
        this.dislikeCount--;
        this.likeStatus = 'none';
      } else {
        // User is disliking - add 1 hour to make it appear newer
        await updateDoc(audioRef, {
          dislikes: arrayUnion(userId),
          likes: arrayRemove(userId), // Remove from likes if previously liked
          timeAdjustment: -3600 // +1 hour in seconds
        });
        this.dislikeCount++;
        // If previously liked, reduce like count
        if (this.likeStatus === 'liked') {
          this.likeCount--;
        }
        this.likeStatus = 'disliked';
      }
    } catch (error) {
      console.error('Error disliking audio:', error);
    }
  }

  async getAudioMetadata(audioId: string): Promise<AudioMetadata | null> {
    try {
      // Create a reference to the document
      const audioDocRef = doc(this._firestore, 'mapAudios', audioId);

      // Get the document
      const docSnap = await getDoc(audioDocRef);

      // Check if document exists
      if (docSnap.exists()) {
        const data = docSnap.data() as any; // First cast to any to avoid type issues
        // Properly handle the timestamp conversion
        return {
          ...data,
          docId: docSnap.id,
          // Convert Firestore timestamps to Date objects with proper type handling
          createdAt: data.createdAt instanceof Date ?
            data.createdAt :
            (data.createdAt && typeof data.createdAt.toDate === 'function' ?
              data.createdAt.toDate() :
              new Date()),
          expirationTime: data.expirationTime instanceof Date ?
            data.expirationTime :
            (data.expirationTime && typeof data.expirationTime.toDate === 'function' ?
              data.expirationTime.toDate() :
              new Date())
        };
      }
      return null;
    } catch (error) {
      console.error('[VideoService] Error getting video metadata:', error);
      return null;
    }
  }


  getVideoStats(videoId: string): Observable<VideoStats> {
    // Create a reference to the document
    const videoDocRef = doc(this._firestore, 'mapVideos', videoId);

    // Create an observable from the document
    return docData(videoDocRef).pipe(
      map(data => {
        if (!data) {
          return { likes: 0, dislikes: 0 };
        }
        // Use type assertion to tell TypeScript that data has these properties
        const docData = data as Record<string, any>;
        return {
          likes: docData['likeCount'] || 0,
          dislikes: docData['dislikeCount'] || 0
        };
      })
    );
  }

  async deleteVideo(videoId: string): Promise<Observable<any>> {
    const user = this._anonymousUserService.currentUserId;
    if (!user) {
      return of({ success: false, error: 'User not authenticated' });
    }

    // Get the video document reference
    const videoDocRef = doc(this._firestore, 'mapVideos', videoId);

    // Get the document data
    const docSnap = await getDoc(videoDocRef);

    // Check if the document exists
    if (!docSnap.exists()) {
      return of({ success: false, error: 'Video not found' });
    }

    // Get the document data
    const data = docSnap.data() as Record<string, any>;

    // Check if the current user is the owner of the video
    if (data && data['userId'] !== user) {
      return of({ success: false, error: 'You do not have permission to delete this video' });
    }

    // Create a batch to delete all related documents
    const batch = writeBatch(this._firestore);

    // Delete the video document
    batch.delete(videoDocRef);

    // Start a chain of operations
    return from(Promise.all([
      // Commit the batch operation
      batch.commit(),

      // Delete the video storage file if it exists
      ...(data && data['videoUrl'] ? [
        deleteObject(ref(this._storage, this.getStoragePathFromUrl(data['videoUrl'])))
          .catch(err => console.error('Error deleting video file:', err))
      ] : []),

      // Delete the thumbnail storage file if it exists and isn't a data URL
      ...(data && data['thumbnailUrl'] && !data['thumbnailUrl'].startsWith('data:') ? [
        deleteObject(ref(this._storage, this.getStoragePathFromUrl(data['thumbnailUrl'])))
          .catch(err => console.error('Error deleting thumbnail file:', err))
      ] : [])
    ])).pipe(
      map(() => ({ success: true }))
    );
  }

  async deleteAudio(audioId: string): Promise<Observable<any>> {
    const user = this._anonymousUserService.currentUserId;
    if (!user) {
      return of({ success: false, error: 'User not authenticated' });
    }

    // Get the audio document reference
    const audioDocRef = doc(this._firestore, 'mapAudios', audioId);

    // Get the document data
    const docSnap = await getDoc(audioDocRef);

    // Check if the document exists
    if (!docSnap.exists()) {
      return of({ success: false, error: 'audio not found' });
    }

    // Get the document data
    const data = docSnap.data() as Record<string, any>;

    // Check if the current user is the owner of the audio
    if (data && data['userId'] !== user) {
      return of({ success: false, error: 'You do not have permission to delete this audio' });
    }

    // Create a batch to delete all related documents
    const batch = writeBatch(this._firestore);

    // Delete the audio document
    batch.delete(audioDocRef);

    // Start a chain of operations
    return from(Promise.all([
      // Commit the batch operation
      batch.commit(),

      // Delete the audio storage file if it exists
      ...(data && data['audioUrl'] ? [
        deleteObject(ref(this._storage, this.getStoragePathFromUrl(data['audioUrl'])))
          .catch(err => console.error('Error deleting audio file:', err))
      ] : []),

      // Delete the thumbnail storage file if it exists and isn't a data URL
      ...(data && data['thumbnailUrl'] && !data['thumbnailUrl'].startsWith('data:') ? [
        deleteObject(ref(this._storage, this.getStoragePathFromUrl(data['thumbnailUrl'])))
          .catch(err => console.error('Error deleting thumbnail file:', err))
      ] : [])
    ])).pipe(
      map(() => ({ success: true }))
    );
  }

  // Helper method to extract storage path from a Firebase Storage URL
  private getStoragePathFromUrl(url: string): string {
    // URL format: https://firebasestorage.googleapis.com/v0/b/[PROJECT_ID].appspot.com/o/[PATH]?token=[TOKEN]
    try {
      const pathname = new URL(url).pathname;
      // Remove '/v0/b/[PROJECT_ID].appspot.com/o/' and decode the rest
      const encodedPath = pathname.split('/o/')[1];
      return decodeURIComponent(encodedPath);
    } catch (e) {
      console.error('Error parsing storage URL:', e);
      return url;
    }
  }

  async setVideoExpiration(videoId: string, expirationTime: Date): Promise<void> {
    try {
      const firestore = getFirestore();
      const videoDocRef = doc(firestore, 'videos', videoId);

      // Get the userId from your user service
      const userId = this._anonymousUserService.currentUserId;

      // Create or update the document
      await setDoc(videoDocRef, {
        expirationTime: expirationTime,
        userId: userId
      }, { merge: true });

    } catch (error) {
      console.error('[VideoService] Error setting video expiration:', error);
      throw error;
    }
  }

  async setAudioExpiration(audioId: string, expirationTime: Date): Promise<void> {
    try {
      const firestore = getFirestore();
      const audioDocRef = doc(firestore, 'audios', audioId);

      // Get the userId from your user service
      const userId = this._anonymousUserService.currentUserId;

      // Create or update the document
      await setDoc(audioDocRef, {
        expirationTime: expirationTime,
        userId: userId
      }, { merge: true });

    } catch (error) {
      console.error('Error setting audio expiration:', error);
      throw error;
    }
  }


  async deleteVideosFromFirestore(videoId: string): Promise<void> {
    try {
      const videoRef = doc(this._firestore, 'mapVideos', videoId);
      await deleteDoc(videoRef);
    } catch (error) {
      console.error('[FirestoreService] Error deleting video:', error);
      throw error;
    }
  }

  async deleteAudiosFromFirestore(audioId: string): Promise<void> {
    try {
      const audioRef = doc(this._firestore, 'mapAudios', audioId);
      await deleteDoc(audioRef);
    } catch (error) {
      console.error('[FirestoreService] Error deleting audio:', error);
      throw error;
    }
  }

  public async listenForNewPhotos(): Promise<void> {
    // If we already have an active listener, don't create another one
    if (this.activeListeners.has('photos')) {
      // console.log('[FirestoreService] Photos listener already active');
      return;
    }

    try {
      //  console.log('[FirestoreService] Setting up photos listener');
      const photosCollection = collection(this._firestore, 'mapPhotos');

      // Set up query for all photos, ordered by timestamp
      const q = query(photosCollection, orderBy('timestamp', 'desc'));

      // Create the listener
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach(change => {

          if (change.type === 'added' || change.type === 'modified') {
            // Get the raw data
            const rawData = change.doc.data();
            const docId = change.doc.id;

            // Debug: Log all available fields in the document
            //  console.log(`[FirestoreService] Full document data for ${docId}:`, rawData);
            //  console.log(`[FirestoreService] Available fields:`, Object.keys(rawData));

            // Check for potential coordinate fields with different names
            const possibleLatFields = ['latitude', 'lat', 'Latitude', 'LAT', 'y', 'Y'];
            const possibleLngFields = ['longitude', 'lng', 'long', 'Longitude', 'LONG', 'x', 'X'];

            let lat = null;
            let lng = null;

            // Try to find latitude field
            for (const field of possibleLatFields) {
              if (rawData[field] !== undefined) {
                // console.log(`[FirestoreService] Found latitude in field: ${field}`);
                lat = parseFloat(rawData[field]);
                break;
              }
            }

            // Try to find longitude field
            for (const field of possibleLngFields) {
              if (rawData[field] !== undefined) {
                //  console.log(`[FirestoreService] Found longitude in field: ${field}`);
                lng = parseFloat(rawData[field]);
                break;
              }
            }

            // If we still don't have coordinates, check for a location object
            if ((lat === null || lng === null) && rawData['location']) {
              //  console.log(`[FirestoreService] Found location object:`, rawData['location']);
              if (rawData['location'].latitude !== undefined && rawData['location'].longitude !== undefined) {
                lat = parseFloat(rawData['location'].latitude);
                lng = parseFloat(rawData['location'].longitude);
              } else if (rawData['location']._lat !== undefined && rawData['location']._long !== undefined) {
                lat = parseFloat(rawData['location']._lat);
                lng = parseFloat(rawData['location']._long);
              } else if (Array.isArray(rawData['location']) && rawData['location'].length >= 2) {
                lat = parseFloat(rawData['location'][0]);
                lng = parseFloat(rawData['location'][1]);
              }
            }

            // Check if we found valid coordinates
            if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) {
              console.error(`[FirestoreService] Could not find valid coordinates in document ${docId}`);
              return; // Skip this document
            }

            // Find the URL field
            const urlField = ['photoUrl', 'url', 'imageUrl', 'src', 'source'].find(f => rawData[f]);
            const photoUrl = urlField ? rawData[urlField] : '';

            if (!photoUrl) {
              console.warn(`[FirestoreService] No photo URL found in document ${docId}`);
            }

            // Prepare the photo data with extracted coordinates
            const photoData = {
              id: docId,
              latitude: lat,
              longitude: lng,
              photoUrl: photoUrl,
              timestamp: rawData['timestamp']
            };

            // console.log('[FirestoreService] Emitting new photo with coordinates:', lat, lng);

            this.newPhotoAdded.next(photoData);
          } else if (change.type === 'removed') {
            // Handle deleted photos
            const docId = change.doc.id;
            //  console.log(`[FirestoreService] Photo deleted: ${docId}`);
            this.photoDeleted.next(docId);
          }
        });
      }, error => {
        console.error('[FirestoreService] Error in photos listener:', error);
      });

      this.activeListeners.set('photos', unsubscribe);
      //  console.log('[FirestoreService] Photos listener successfully set up');
    } catch (error) {
      console.error('[FirestoreService] Error setting up photos listener:', error);
    }
  }

  public async listenForNewVideos(lastTimestampPromise?: Promise<number | undefined>): Promise<void> {
    // If we already have an active listener, don't create another one
    if (this.activeListeners.has('videos')) {
      console.log('[FirestoreService] videos listener already active');
      return;
    }

    try {
      //  console.log('[FirestoreService] Setting up videos listener');
      const videosCollection = collection(this._firestore, 'mapVideos');

      // Set up query for all videos, ordered by timestamp
      const q = query(videosCollection, orderBy('timestamp', 'desc'));

      // Create the listener
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach(change => {

          if (change.type === 'added' || change.type === 'modified') {
            // Get the raw data
            const rawData = change.doc.data();
            const docId = change.doc.id;

            // Debug: Log all available fields in the document
            //  console.log(`[FirestoreService] Full document data for ${docId}:`, rawData);
            //  console.log(`[FirestoreService] Available fields:`, Object.keys(rawData));

            // Check for potential coordinate fields with different names
            const possibleLatFields = ['latitude', 'lat', 'Latitude', 'LAT', 'y', 'Y'];
            const possibleLngFields = ['longitude', 'lng', 'long', 'Longitude', 'LONG', 'x', 'X'];

            let lat = null;
            let lng = null;

            // Try to find latitude field
            for (const field of possibleLatFields) {
              if (rawData[field] !== undefined) {
                //  console.log(`[FirestoreService] Found latitude in field: ${field}`);
                lat = parseFloat(rawData[field]);
                break;
              }
            }

            // Try to find longitude field
            for (const field of possibleLngFields) {
              if (rawData[field] !== undefined) {
                //  console.log(`[FirestoreService] Found longitude in field: ${field}`);
                lng = parseFloat(rawData[field]);
                break;
              }
            }

            // If we still don't have coordinates, check for a location object
            if ((lat === null || lng === null) && rawData['location']) {
              // console.log(`[FirestoreService] Found location object:`, rawData['location']);
              if (rawData['location'].latitude !== undefined && rawData['location'].longitude !== undefined) {
                lat = parseFloat(rawData['location'].latitude);
                lng = parseFloat(rawData['location'].longitude);
              } else if (rawData['location']._lat !== undefined && rawData['location']._long !== undefined) {
                lat = parseFloat(rawData['location']._lat);
                lng = parseFloat(rawData['location']._long);
              } else if (Array.isArray(rawData['location']) && rawData['location'].length >= 2) {
                lat = parseFloat(rawData['location'][0]);
                lng = parseFloat(rawData['location'][1]);
              }
            }

            // Check if we found valid coordinates
            if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) {
              console.error(`[FirestoreService] Could not find valid coordinates in document ${docId}`);
              return; // Skip this document
            }

            // Find the URL field
            const urlField = ['videoUrl', 'url', 'imageUrl', 'src', 'source'].find(f => rawData[f]);
            const videoUrl = urlField ? rawData[urlField] : '';

            if (!videoUrl) {
              console.warn(`[FirestoreService] No video URL found in document ${docId}`);
            }

            // Prepare the video data with extracted coordinates
            const videoData = {
              id: docId,
              latitude: lat,
              longitude: lng,
              videoUrl: videoUrl,
              timestamp: rawData['timestamp']
            };

            // console.log('[FirestoreService] Emitting new video with coordinates:', lat, lng);

            this.newVideoAdded.next(videoData);
          } else if (change.type === 'removed') {
            // Handle deleted videos
            const docId = change.doc.id;
            //  console.log(`[FirestoreService] video deleted: ${docId}`);
            this.videoDeleted.next(docId);
          }
        });
      }, error => {
        console.error('[FirestoreService] Error in videos listener:', error);
      });

      this.activeListeners.set('videos', unsubscribe);
      //  console.log('[FirestoreService] videos listener successfully set up');
    } catch (error) {
      console.error('[FirestoreService] Error setting up videos listener:', error);
    }
  }

  public async listenForNewAudios(lastTimestampPromise?: Promise<number | undefined>): Promise<void> {
    // If we already have an active listener, don't create another one
    if (this.activeListeners.has('audios')) {
      // console.log('[FirestoreService] audios listener already active');
      return;
    }

    try {
      //  console.log('[FirestoreService] Setting up audios listener');
      const audiosCollection = collection(this._firestore, 'mapAudios');

      // Set up query for all audios, ordered by timestamp
      const q = query(audiosCollection, orderBy('timestamp', 'desc'));

      // Create the listener
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach(change => {

          if (change.type === 'added' || change.type === 'modified') {
            // Get the raw data
            const rawData = change.doc.data();
            const docId = change.doc.id;

            // Debug: Log all available fields in the document
            //   console.log(`[FirestoreService] Full document data for ${docId}:`, rawData);
            //   console.log(`[FirestoreService] Available fields:`, Object.keys(rawData));

            // Check for potential coordinate fields with different names
            const possibleLatFields = ['latitude', 'lat', 'Latitude', 'LAT', 'y', 'Y'];
            const possibleLngFields = ['longitude', 'lng', 'long', 'Longitude', 'LONG', 'x', 'X'];

            let lat = null;
            let lng = null;

            // Try to find latitude field
            for (const field of possibleLatFields) {
              if (rawData[field] !== undefined) {
                //  console.log(`[FirestoreService] Found latitude in field: ${field}`);
                lat = parseFloat(rawData[field]);
                break;
              }
            }

            // Try to find longitude field
            for (const field of possibleLngFields) {
              if (rawData[field] !== undefined) {
                //  console.log(`[FirestoreService] Found longitude in field: ${field}`);
                lng = parseFloat(rawData[field]);
                break;
              }
            }

            // If we still don't have coordinates, check for a location object
            if ((lat === null || lng === null) && rawData['location']) {
              //  console.log(`[FirestoreService] Found location object:`, rawData['location']);
              if (rawData['location'].latitude !== undefined && rawData['location'].longitude !== undefined) {
                lat = parseFloat(rawData['location'].latitude);
                lng = parseFloat(rawData['location'].longitude);
              } else if (rawData['location']._lat !== undefined && rawData['location']._long !== undefined) {
                lat = parseFloat(rawData['location']._lat);
                lng = parseFloat(rawData['location']._long);
              } else if (Array.isArray(rawData['location']) && rawData['location'].length >= 2) {
                lat = parseFloat(rawData['location'][0]);
                lng = parseFloat(rawData['location'][1]);
              }
            }

            // Check if we found valid coordinates
            if (lat === null || lng === null || isNaN(lat) || isNaN(lng)) {
              console.error(`[FirestoreService] Could not find valid coordinates in document ${docId}`);
              return; // Skip this document
            }

            // Find the URL field
            const urlField = ['audioUrl', 'url', 'imageUrl', 'src', 'source'].find(f => rawData[f]);
            const audioUrl = urlField ? rawData[urlField] : '';

            if (!audioUrl) {
              console.warn(`[FirestoreService] No audio URL found in document ${docId}`);
            }

            // Prepare the audio data with extracted coordinates
            const audioData = {
              id: docId,
              latitude: lat,
              longitude: lng,
              audioUrl: audioUrl,
              timestamp: rawData['timestamp']
            };

            // console.log('[FirestoreService] Emitting new audio with coordinates:', lat, lng);

            this.newAudioAdded.next(audioData);
          } else if (change.type === 'removed') {
            // Handle deleted audios
            const docId = change.doc.id;
            // console.log(`[FirestoreService] audio deleted: ${docId}`);
            this.audioDeleted.next(docId);
          }
        });
      }, error => {
        console.error('[FirestoreService] Error in audios listener:', error);
      });

      this.activeListeners.set('audios', unsubscribe);
      // console.log('[FirestoreService] audios listener successfully set up');
    } catch (error) {
      console.error('[FirestoreService] Error setting up audios listener:', error);
    }
  }


  public stopAllListeners(): void {
    this.activeListeners.forEach((unsubscribe, key) => {
      unsubscribe();
      this.activeListeners.delete(key);
    });
  }


  public stopListener(type: 'photos' | 'videos' | 'audios'): void {
    if (this.activeListeners.has(type)) {
      const unsubscribe = this.activeListeners.get(type);
      if (unsubscribe) {  // Additional null check to satisfy TypeScript
        unsubscribe();
      }
      this.activeListeners.delete(type);
    }
  }

  public async listenForAllPhotos(): Promise<void> {
    const photosCollection = collection(this._firestore, 'mapPhotos');

    // Listen to the collection without timestamp filtering
    onSnapshot(photosCollection, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          const photoData = {
            id: change.doc.id,
            ...change.doc.data()
          };
          this.newPhotoAdded.next(photoData);
        }
      });
    }, error => {
      console.error('Error listening for photos:', error);
    });
  }


  public async listenForAllVideos(): Promise<void> {
    const videosCollection = collection(this._firestore, 'mapVideos');

    onSnapshot(videosCollection, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          const videoData = {
            id: change.doc.id,
            ...change.doc.data()
          };
          this.newVideoAdded.next(videoData);
        }
      });
    }, error => {
      console.error('Error listening for videos:', error);
    });
  }


  public async listenForAllAudios(): Promise<void> {
    const audiosCollection = collection(this._firestore, 'mapFudios');

    onSnapshot(audiosCollection, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added' || change.type === 'modified') {
          const audioData = {
            id: change.doc.id,
            ...change.doc.data()
          };
          this.newAudioAdded.next(audioData);
        }
      });
    }, error => {
      console.error('Error listening for audios:', error);
    });
  }
}